import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormControl,
  Grid as Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { useEffect, useRef, useState } from "react";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { Add, Cancel, Check, Delete, Edit } from "@mui/icons-material";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

export default function TicketTailorMappingEditor({ modelName }) {
  const { data: existingMappingData, isLoading: mapLoading } = useGet(
    apiUrls.admin.ticketTailorQuestionMapping(modelName),
    {
      params: {
        size: 1,
      },
      staleTime: 0,
    }
  );
  const { data: modelIntrospectData, isLoading: introspectLoading } = useGet(
    apiUrls.introspect(modelName)
  );
  const [mapState, setMapState] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const initialMapState = useRef(null);

  useEffect(() => {
    if (existingMappingData && !mapState) {
      const newMapState = Object.entries(existingMappingData.data.mapping).map(
        ([question, field]) => ({
          question: question,
          field: field,
        })
      );
      setMapState(newMapState);

      // Running the same code again so that I don't have to deep copy here
      initialMapState.current = Object.entries(
        existingMappingData.data.mapping
      ).map(([question, field]) => ({
        question: question,
        field: field,
      }));
    }
  }, [existingMappingData, mapState]);

  if (mapLoading || introspectLoading || !mapState) {
    return (
      <Box
        sx={{ height: "100%" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  const fieldOptions = modelIntrospectData.fields.filter(
    (fieldInfo) =>
      ["CharField", "TextField", "IntegerField", "FloatField"].includes(
        fieldInfo.type
      ) & !["uuid", "created_at"].includes(fieldInfo.name)
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newMapping = Object.fromEntries(
      mapState.map(({ question, field }) => [question, field])
    );
    await api.post(apiUrls.admin.ticketTailorQuestionMapping(modelName), {
      mapping: newMapping,
    });
    showSuccessSnackbar("Saved!");
    setIsEditing(false);
    initialMapState.current = JSON.parse(JSON.stringify(mapState));
  };

  const handleEditField = (index, field) => {
    setMapState((currentMapState) => {
      const newMapState = Array.from(currentMapState);
      newMapState[index].field = field;
      return newMapState;
    });
  };

  const handleEditQuestion = (index, question) => {
    setMapState((currentMapState) => {
      const newMapState = Array.from(currentMapState);
      newMapState[index].question = question;
      return newMapState;
    });
  };

  const formatField = (fieldName) => {
    return fieldName.split("_").map(capitalize).join(" ");
  };

  const usedFields = mapState.map(({ field }) => field);
  if (isEditing) {
    return (
      <Box component={"form"} onSubmit={handleSubmit}>
        <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
          Ticket Tailor Question Mapping{" "}
        </Typography>
        <ButtonGroup sx={{ my: 1 }} fullWidth>
          <Button size="small" startIcon={<Check />} type="submit">
            Save
          </Button>
          <Button
            size="small"
            startIcon={<Cancel />}
            onClick={() => {
              setMapState(initialMapState.current);
              setIsEditing(false);
            }}
          >
            Cancel
          </Button>
        </ButtonGroup>
        <Grid container gap={1} onSubmit={handleSubmit}>
          {mapState.map((fieldInfo, i) => (
            <Grid
              key={i}
              size={12}
              container
              spacing={2}
              alignItems={"center"}
              sx={{ pr: 1 }}
            >
              <Grid size={6}>
                <TextField
                  label="Ticket Tailor Question"
                  variant="standard"
                  value={fieldInfo.question}
                  onChange={(event) =>
                    handleEditQuestion(i, event.target.value)
                  }
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={5}>
                <FormControl fullWidth variant="standard">
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={fieldInfo.field}
                    label="Field"
                    required
                    onChange={(event) => handleEditField(i, event.target.value)}
                  >
                    <MenuItem key={fieldInfo.field} value={fieldInfo.field}>
                      {formatField(fieldInfo.field)}
                    </MenuItem>
                    {fieldOptions
                      .filter(
                        (fieldOption) => !usedFields.includes(fieldOption.name)
                      )
                      .map((fieldOption) => (
                        <MenuItem
                          key={fieldOption.name}
                          value={fieldOption.name}
                        >
                          {formatField(fieldOption.name)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              {(modelName != "StudentProfile" ||
                !["email", "first_name", "last_name", "phone"].includes(
                  fieldInfo.field
                )) && (
                <Grid size={1}>
                  <IconButton
                    onClick={() => {
                      const copy = Array.from(mapState);
                      copy.splice(i, 1);
                      setMapState(copy);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          ))}
        </Grid>
        <Button
          variant="standard"
          size="small"
          startIcon={<Add />}
          fullWidth
          sx={{ mt: 2 }}
          onClick={() => {
            setMapState((currMapState) => [
              ...currMapState,
              {
                field: "",
                question: "",
              },
            ]);
          }}
        >
          Add Question
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
        Ticket Tailor Question Mapping{" "}
        <Chip
          label="Edit"
          size="small"
          icon={<Edit />}
          onClick={() => setIsEditing(true)}
        />
      </Typography>
      <List>
        {mapState.map((fieldInfo, i) => (
          <ListItem key={i} divider>
            <ListItemText
              primary={fieldInfo.question}
              secondary={`Field: ${formatField(fieldInfo.field)}`}
              primaryTypographyProps={{
                sx: {
                  fontWeight: "bold",
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
